/**
 * Checks whether an arguments is null.
 *
 * @param The values to be checked.
 *
 * @returns true if any argument is null.
 */
const isNull = function isNull(...args: any[]): boolean {
  if (args.length === 0) {
    return true;
  }
  const len: number = args.length;
  for (let i = 0; i < len; i++) {
    const obj: any = args[i];
    if (obj === undefined || obj === null) {
      return true;
    }
  }
  return false;
};

/**
 * Checks whether all of the provided arguments are NOT null.
 *
 * @param values to be checked.
 *
 * @returns true if all arguments are NOT null.
 */
const isNotNull = function isNotNull(...args: any[]): boolean {
  return !isNull(...args); // forward args
};

const hasEnumerablePropertiesInternal =
  function hasEnumerablePropertiesInternal(obj: any): boolean {
    return !(
      obj instanceof Date ||
      typeof obj === "boolean" ||
      typeof obj === "number"
    );
  };

const isEmptyObjInternal = function isEmptyObjInternal(obj: any): boolean {
  for (const name in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, "name")) {
      return false;
    }
  }
  return true;
};

/**
 * @private
 */
const emptyInternal = function emptyInternal(obj: any): boolean {
  if (typeof obj === "object") {
    /* Objects like Number, Date, Boolean
     * are never null.
     * Since they contain only non enumerable
     * properties, they can never be caught in
     * the property for..in loop in _isEmptyObj
     */
    if (hasEnumerablePropertiesInternal(obj)) {
      return isEmptyObjInternal(obj);
    }
    return false;
  } else {
    return obj.length === 0;
  }
};

/**
 * Checks whether any of the provided arguments are empty.
 *
 * @param Values to be checked.
 *
 * @returns true if any argument is empty.
 */
const isEmpty = function isEmpty(...args: any[]): boolean {
  if (isNull(...args)) {
    // forward args
    return true;
  }
  const len: number = args.length;
  for (let i = 0; i < len; i++) {
    if (emptyInternal(args[i])) {
      return true;
    }
  }
  return false;
};

/**
 * Checks whether given arguments are not empty.
 *
 * @param Values to be checked.
 *
 * @returns true if arguments are not empty.
 */
const isNotEmpty = function isNotEmpty(...args: any[]): boolean {
  return !isEmpty(...args); // forward args
};

/**
 * Gives the first non-null value in the arguments list.
 *
 * @param Values to be checked.
 *
 * @returns Returns the first non-null value in the arguments list.
 */
const coalesce = function coalesce(...args: any[]): any {
  if (args.length === 0) {
    return null;
  }
  const len: number = args.length;
  let arg: any = null;
  for (let i = 0; i < len; i++) {
    arg = args[i];
    if (isNotNull(arg)) {
      break;
    }
  }
  return arg;
};

export default {
  isNull,
  isNotNull,
  isEmpty,
  isNotEmpty,
  coalesce
};
